import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'setup/redux/Hooks'
import { selectUser, selectLanguage } from 'app/modules/auth'
import { PageUrl } from 'app/utils/common/constants'
import { formatDateByLocale } from 'app/utils/helpers/OrderDrawerHelpers'
import { CountdownReminderModal } from 'app/utils/helpers/components/CountdownReminderModal'
import { paymentsActions, selectZatcaIntegrationStatus } from '../redux'
import { getZatcaStatusTone, getZatcaRenewalDaysLeft } from './zatcaStatusTone'

const RENEWAL_REMINDER_SESSION_KEY_PREFIX = 'zatcaRenewalReminderShown_'

export const GlobalRenewalReminder = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const history = useHistory()
  const language = useAppSelector(selectLanguage)
  const user = useAppSelector(selectUser)
  const status = useAppSelector(selectZatcaIntegrationStatus)

  const isClusterLogic = !!user?.companyId && !!user?.isWatanCompany

  const [show, setShow] = useState(false)
  const hasAutoShownRef = useRef(false)

  useEffect(() => {
    if (isClusterLogic) {
      dispatch(paymentsActions.getZatcaIntegrationStatus())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClusterLogic])

  useEffect(() => {
    if (status && !hasAutoShownRef.current && getZatcaStatusTone(status).key === 'soon') {
      hasAutoShownRef.current = true

      // Persisted per tab session (not per component mount) so a page refresh doesn't
      // re-trigger the popup; keyed by renewalDue so a new reminder period shows again.
      const sessionKey = `${RENEWAL_REMINDER_SESSION_KEY_PREFIX}${user?.companyId}_${status.renewalDue}`
      let alreadyShownThisSession = false
      try {
        alreadyShownThisSession = sessionStorage.getItem(sessionKey) === '1'
      } catch {
        // sessionStorage unavailable (e.g. privacy mode) - fall back to showing it
      }

      if (!alreadyShownThisSession) {
        setShow(true)
        try {
          sessionStorage.setItem(sessionKey, '1')
        } catch {
          // ignore storage errors
        }
      }
    }
  }, [status, user?.companyId])

  if (!isClusterLogic || !status) {
    return null
  }

  const daysLeft = getZatcaRenewalDaysLeft(status)
  if (daysLeft === null) {
    return null
  }

  const formatDate = (value?: string) => (value ? formatDateByLocale(value, language, 'DD MMM YYYY') : '')

  return (
    <CountdownReminderModal
      show={show}
      onHide={() => setShow(false)}
      icon="⏱"
      title={t('ZATCA_INTEGRATION_PAGE.STATUS.RENEWAL_REMINDER_TITLE')}
      countValue={daysLeft}
      countLabel={t('ZATCA_INTEGRATION_PAGE.STATUS.RENEWAL_REMINDER_DAYS_LEFT')}
      description={t('ZATCA_INTEGRATION_PAGE.STATUS.RENEWAL_REMINDER_DESCRIPTION', {
        organizationName: status.organizationName,
        date: formatDate(status.renewalDue),
      })}
      primaryLabel={<>⟳ {t('ZATCA_INTEGRATION_PAGE.STATUS.RENEW_NOW')}</>}
      onPrimaryClick={() => {
        setShow(false)
        history.push(PageUrl.PAYMENTS.ZATCA_INTEGRATION)
      }}
      secondaryLabel={t('ZATCA_INTEGRATION_PAGE.STATUS.REMIND_LATER')}
      onSecondaryClick={() => setShow(false)}
    />
  )
}

export default GlobalRenewalReminder
